import { useState, useMemo, useEffect } from "react";
import './EcomPage.css';
import {
    TextField,
    Button,
    Container,
    styled,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment,
    SelectChangeEvent,
    CircularProgress,
} from "@mui/material";
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import { tableCellClasses } from '@mui/material/TableCell';
import TableMenuButton from "components/button/TableMenuButton";
import FigmaModal from "components/modal/FigmaModal";
import { useLocation } from 'react-router-dom';
import { useGetMaterialTypesQuery, useGetEcomQuery, useDeleteEcomMutation, useSetActiveEcomMutation } from "api/api";
import { StyledTableCell as BaseStyledTableCell, StyledTableRow, TableLoadingRow } from "components/tables/styledTableComponents";
import { getErrorMessage } from "lib/utils/getErrorMessage";
import ErrorModal from "components/modal/ErrorModal";

type ModalType = 'addFirm' | 'addFirmSuccess' | 'addProject' | 'addEcomSuccess';

export const StyledTableCell = styled(BaseStyledTableCell)(() => ({
    // Extend the base style to match the page specific styling
    [`&.${tableCellClasses.body}`]: {
        maxHeight: '73px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}));

const downArrowPath = '/assets/icons/ArrowsDown.svg';
const upArrowPath = '/assets/icons/ArrowsUp.svg';
const upDownArrowPath = '/assets/icons/ArrowsDownUp.svg';

const EcomPage = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState<ModalType>('addProject');
    const [sortConfig, setSortConfig] = useState({ key: 'version', direction: 'ascending' });
    const location = useLocation();

    const [deleteEcom, { error: deleteError, reset: resetDelete }] = useDeleteEcomMutation();
    const [setActiveEcom, { error: setActiveError, reset: resetSetActive }] = useSetActiveEcomMutation();

    const error = deleteError || setActiveError;

    const handleCloseErrorModal = () => {
        resetDelete();
        resetSetActive();
    };

    const { data: materialTypes = [], isLoading: loadingMaterialTypes } = useGetMaterialTypesQuery();
    const { data: ecomData = [], refetch, isLoading: loadingEcomData } = useGetEcomQuery();
    const columnList = ['Version', 'Version Created', 'Created By'].concat(materialTypes.map(item => `${item.category}: ${item.name}`));
    console.log('ecomData = ', ecomData)

    const handleSetActive = async (ecom: any) => {
        try {
            if (ecom.version) {
                await setActiveEcom(ecom.version).unwrap();
            }
        } catch (e) {
            console.error('Set active ECOM error:', e);
        }
    };

    const sortedData = useMemo(() => {
        let sortableItems = [...ecomData];
        // If the sortConfig.key is CATEGORY: MATERIAL_TYPE
        // Need to identify the ECOMData item that shares the CATEGORY and TYPE
        // Then, need to compare a1_a3_carbon_factor value
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                let aValue;
                let bValue;
                // If the column to sort is one of the material type columns
                if (sortConfig.key.includes(':')) {
                    let targetMaterialCategory = sortConfig.key.split(':')[0].trim();
                    let targetMaterialType = sortConfig.key.split(':')[1].trim();
                    aValue = a.ECOMData?.find(
                        (ecomItem) =>
                            ecomItem.material_category === targetMaterialCategory &&
                            ecomItem.material_type === targetMaterialType
                    )?.a1_a3_carbon_factor ?? null;
                    bValue = b.ECOMData?.find(
                        (ecomItem) =>
                            ecomItem.material_category === targetMaterialCategory &&
                            ecomItem.material_type === targetMaterialType
                    )?.a1_a3_carbon_factor ?? null;
                } else {
                    aValue = a[sortConfig.key];
                    bValue = b[sortConfig.key];
                }
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return sortConfig.direction === 'ascending'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [ecomData, sortConfig]);

    const reformatKey = (key) => {
        if (key) {
            let reformattedKey = key;
            if (key == 'Version') {
                reformattedKey = 'version';
            } else if (key == 'Version Created') {
                reformattedKey = 'created_at';
            } else if (key == 'Created By') {
                reformattedKey = 'created_by';
            } else {
                reformattedKey = key;
            }
            return reformattedKey;
        }
    };

    const handleSortTable = (key) => {
        let direction = 'ascending';
        if (sortConfig.key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        const reformattedKey = reformatKey(key);
        setSortConfig({ key: reformattedKey, direction });
    };

    const getSortIcon = () => {
        const arrowUp = <img src={upArrowPath} alt="Sort Ascending"></img>
        const arrowDown = <img src={downArrowPath} alt="Sort Descending"></img>
        const arrowUpDown = <img src={upDownArrowPath} alt="Sort"></img>

        if (sortConfig.key) {
            return sortConfig.direction === 'ascending' ? arrowUp : arrowDown;
        }
        return arrowUpDown;
    };

    // To pass to TableMenuButton's onDelete()
    const refreshData = () => {
        refetch();
        // console.log("Refetched data");
    };

    // Modal functions
    const handleOpenModal = () => {
        setIsModalOpen(true);
    }

    const handleCloseModal = () => {
        setIsModalOpen(false);
    }

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const modal = queryParams.get('modal');
        const modalTypeParam = queryParams.get('modalType');
        refetch();
        if (modal === 'open' && modalTypeParam) {
            setIsModalOpen(true);
            if (modalTypeParam === 'addEcomSuccess') {
                setModalType(modalTypeParam as ModalType);
            }
        }
    }, [location]);

    return (
        <StyledContainer>
            <FigmaModal open={isModalOpen} onClose={handleCloseModal} type={modalType} />
            <ErrorModal
                open={!!error}
                onClose={handleCloseErrorModal}
                errorMessage={error ? getErrorMessage(error) : null}
            />
            <Box sx={{
                display: 'flex',
            }}>
                <Button
                    type="button"
                    // onClick={handleOpenModal}
                    component={Link}
                    to="/add-ecom/1"
                    variant="outlined"
                    sx={{ mr: 2, borderRadius: 12 }}
                    startIcon={<AddIcon />}
                >
                    Add New ECOM Factor
                </Button>
            </Box>
            <div style={{ maxWidth: '90vw', width: '78vw', height: '100%', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingTop: 10, marginTop: 20 }}>
                <Box sx={{ width: '100%', position: 'relative' }}>
                    {loadingMaterialTypes ?
                        <div style={{
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            paddingTop: 10
                        }}>
                            <CircularProgress size={50} />
                        </div>
                        :
                        <>
                            <div style={{ width: '100%', height: '100%', color: '#555555', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontWeight: '700', wordWrap: 'break-word', marginBottom: '10px' }}>Search</div>
                            <TextField
                                placeholder="Search ..."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    style: {
                                        borderRadius: 22,
                                        height: "44px",
                                        width: "343px",
                                        marginRight: "16px",
                                    }
                                }}
                            />
                            <Box sx={{ position: 'relative', overflowX: 'auto', width: '100%' }}>
                                <TableContainer
                                    component={Paper}
                                    className="custom-scrollbar"
                                    sx={{
                                        mt: "20px",
                                        overflowX: 'auto',
                                        maxWidth: '84vw',
                                        position: 'relative',
                                    }}
                                >
                                    <div style={{ width: '467.2%', height: '4px', background: 'linear-gradient(90deg, #059BD6 0%, #9FD64D 100%)' }} />
                                    <Table>
                                        <TableHead>
                                            <StyledTableRow >
                                                {/* ['version', 'versionCreated', 'createdBy', 'psiLwcConcrete', 'psiConcrete', 'steelFabricatedRebar', 'steelReinforcementRebar'] */}
                                                {columnList.map(key => (
                                                    <StyledTableCell
                                                        key={key}
                                                        onClick={() => handleSortTable(key)}
                                                        className={sortConfig.key === key ? 'sorted' : ''}
                                                    >
                                                        {key}
                                                        <span className="sort-icon" >
                                                            {getSortIcon()}
                                                        </span>
                                                    </StyledTableCell>
                                                ))}
                                                <StyledTableCell sx={{
                                                    position: 'sticky !important',
                                                    right: 0,
                                                    zIndex: 100,
                                                }}>
                                                    <SettingsIcon sx={{
                                                        display: 'flex',
                                                        alignSelf: 'center'
                                                    }} />
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loadingEcomData ?
                                                <TableLoadingRow colSpan={columnList.length + 1} />
                                                :
                                                sortedData.map((ecom, index) => (
                                                    <StyledTableRow key={index}>
                                                        <StyledTableCell key={index + '-' + ecom.version}>{ecom.version}{ecom.is_active ? " - Active" : null}</StyledTableCell >
                                                        <StyledTableCell key={index + '-' + ecom.created_at}>{ecom.created_at}</StyledTableCell >
                                                        <StyledTableCell key={index + '-' + ecom.created_by}>{ecom.created_by}</StyledTableCell >
                                                        {ecom.ECOMData?.map((ecomData) => (
                                                            <StyledTableCell key={index + '-' + ecomData.material_category + '-' + ecomData.material_type}>{ecomData.a1_a3_carbon_factor}</StyledTableCell >
                                                        ))}
                                                        <StyledTableCell style={{ overflow: 'visible', position: 'sticky', right: 0, padding: 0 }}>
                                                            <TableMenuButton
                                                                index={index}
                                                                tableContext="ecom"
                                                                rowData={ecom}
                                                                onDelete={refreshData}
                                                                deleteMutation={deleteEcom}
                                                                onEdit={() => handleSetActive(ecom)}
                                                            />
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </>
                    }
                </Box>
            </div>
        </StyledContainer>
    );
};

export default EcomPage;

const StyledContainer = styled(Container)({
    marginBottom: 24,
});